import { useEffect, useRef, useState } from 'react'
import { CellularPayPrompt, PromptView } from './types'
import { forgetOtherInvoices, isRemindedLater } from './remind-later'
import { isLocked } from './is-locked'
import { isShutOff } from './sim-status'
import { pendingAch, trustedPendingAch } from './ach'

// A CMS tab stays open for days. Re-check often enough that a 24 hour snooze
// expires by itself, and that a prompt which has become blocking starts blocking,
// without the customer reloading the page.
export const RECHECK_INTERVAL_MS = 5 * 60 * 1000

const initialView = (prompt: CellularPayPrompt | null): PromptView => {
  // A bill whose SIMs are all shut off has nothing left to pay: only its banner.
  if (!prompt || prompt.gaveUp || isShutOff(prompt)) return 'closed'
  // A bank payment is on its way: it counts as paid, so nothing pops up (its banner says so).
  // A later one on the bill does not count: the bill locks as any owed bill.
  if (trustedPendingAch(prompt)) return 'closed'
  // 'closed' is the banner, not nothing. Only the back-bill catch-up opens a dialog
  // on its own: a renewal is a reminder, and a pop-up over someone's work is not.
  if (!prompt.isBackbill) return 'closed'
  // A snooze cannot silence a lock, however it came to be stored: a bill that was
  // snoozed while it was only a reminder still blocks once it turns blocking.
  if (!isLocked(prompt) && isRemindedLater(prompt.invoiceId)) return 'closed'
  return 'owed'
}

interface UsePromptViewArgs {
  prompt: CellularPayPrompt | null
  // Refetches the prompt, so a bill that has turned blocking is noticed.
  refresh: () => void
}

export const usePromptView = ({ prompt, refresh }: UsePromptViewArgs) => {
  const [view, setViewState] = useState<PromptView>('closed')
  const latest = useRef({ prompt, view, refresh })

  useEffect(() => {
    latest.current = { prompt, view, refresh }
  })

  // Closing a locked bill would leave the customer with no dialog, no way back to it
  // and only a dismissible bar to show for it, which is not the lock we promised.
  // Paying or giving up clears it by changing the prompt itself, not this view.
  const setView = (next: PromptView): void =>
    setViewState(next === 'closed' && isLocked(latest.current.prompt) ? 'owed' : next)

  const promptKey = prompt
    ? `${prompt.invoiceId}:${prompt.gaveUp}:${isLocked(prompt)}:${pendingAch(prompt)?.state || ''}`
    : ''
  useEffect(() => {
    setView(initialView(prompt))
    if (prompt) forgetOtherInvoices(prompt.invoiceId)
    // Reset only when a different invoice or state arrives, not on every refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptKey])

  useEffect(() => {
    const recheck = () => {
      const current = latest.current
      // A locked customer may have paid on Stripe's hosted page in another tab: ask
      // again, but leave the dialog they are on (and any form in it) as it is. The
      // lock lifts when the refreshed prompt says the bill is paid.
      if (isLocked(current.prompt)) {
        current.refresh()
        return
      }
      // Never pull the ground from under a customer who has a dialog open.
      if (current.view !== 'closed') return
      setView(initialView(current.prompt))
      current.refresh()
    }
    const timer = window.setInterval(recheck, RECHECK_INTERVAL_MS)
    window.addEventListener('focus', recheck)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', recheck)
    }
  }, [])

  return { view, setView }
}
